import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AgEditorComponent } from '../../../../global/gx-ag-grid/ag-editor.component';
import { CustomService, Location } from '../../../../../models/locations-models/location';
import { FeedbackService } from '../../../../../core/feedback.service';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ICellEditorParams } from 'ag-grid-community';
import { CustomServicesTControl } from '../../../../../models/locations-models/edit-location-form';
import { customServicesValidators, ValidationErrorKeys } from '../../../edit-location/service/validators';
import { getFullNameForFieldID } from '../../../location-utils';
import { capitalizeFirstLetter } from '../../../../../core/utilities';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { LetDirective } from '@ngrx/component';

@Component({
    selector: 'app-custom-services-editor',
    templateUrl: './custom-services-editor.component.html',
    styleUrls: ['../simple-text-editor/simple-text-editor.component.scss',
                './custom-services-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        LetDirective,
        CdkTrapFocus,
        FormsModule,
        ReactiveFormsModule,
        NgClass,
        MatFormField,
        MatLabel,
        MatInput,
        GxInputDirective,
        MatError,
        MatTooltip,
        MatIcon,
        CdkTextareaAutosize,
        MatDivider,
        MatButton,
    ],
})
export class CustomServicesEditorComponent extends AgEditorComponent<Location>{
  @ViewChildren('serviceNameInput') serviceNameInput: QueryList<ElementRef>;
  @ViewChild('createButton') createButton: MatButton;

  constructor(protected feedbackService: FeedbackService,
              protected cd: ChangeDetectorRef,
              private fb: FormBuilder,
              private clipboard: Clipboard) {
    super(feedbackService, cd);
  }

  protected override createEditorFormGroup(params: ICellEditorParams<Location>):
      FormGroup<{customServices: FormArray<FormGroup<CustomServicesTControl>>}> {

    let customServicesFormArray = this.fb.array([]) as unknown as
        FormArray<FormGroup<CustomServicesTControl>>;

    params?.data?.customServices?.forEach(customService => {
      customServicesFormArray.push(this.fb.group(
          {
            'serviceName': [
              customService.serviceName,
              customServicesValidators.serviceName,
            ],
            'serviceDescription': [
              customService.serviceDescription,
              customServicesValidators.serviceDescription,
            ],
            'serviceCategoryId': [
              customService.serviceCategoryId,
              customServicesValidators.serviceCategoryId,
            ],
          },
      ));
    });

    return this.fb.group({
      customServices: customServicesFormArray
    })
  }

  protected override getNewFieldValue(): CustomService[] {
    return this.getCustomServicesFormArray()
        ?.getRawValue()
        ?.map(rw => ({
          ...rw,
          serviceDescription: rw.serviceDescription ? rw.serviceDescription : null,
          serviceCategoryId: rw.serviceCategoryId ? rw.serviceCategoryId : null
        }));
  };

  /**
   * A hook to perform any necessary operation just after the GUI for this component has been
   * rendered on the screen.
   * This method is called each time the edit component is activated.
   * This is useful for any logic that requires attachment before executing, such as putting focus
   * on a particular DOM element.
   */
  public afterGuiAttached(): void {
    if (this.serviceNameInput?.first) {
      this.serviceNameInput
          ?.first
          ?.nativeElement
          ?.focus();
    } else {
      this.createButton?.focus();
    }
  }

  /**
   *  Adds a new custom service pair to the formArray
   */
  protected addCustomService() {
    this.getCustomServicesFormArray().push(
        this.createCustomServiceFormGroup());
    // also, focus and scroll to the last added group's native input
    // but wait a bit for the render of said last group to go through
    setTimeout(() => {
      this.serviceNameInput
          ?.last
          ?.nativeElement
          ?.focus();
    }, 200);
  }

  /**
   * Remove a custom service pair from the formArray and focus the next pair in line or the create
   * button.
   * @param i
   */
  protected deleteCustomService(i: number) {
    const isLast = this.getCustomServicesFormArray()?.controls?.length - 1 === i;
    this.getCustomServicesFormArray().removeAt(i);

    // Setting focus is important as it is lost during removing and then the component no longer listens
    // for key presses (ENTER/ESCAPE no longer works)
    setTimeout(() => {
      if (!this.getCustomServicesFormArray()?.controls?.length) {
        this.createButton?.focus();
      } else {
        this.serviceNameInput.get(isLast ? i-1 : i)?.nativeElement?.focus({ preventScroll: true })
      }
    }, 200);
  }

  /**
   * Copy custom service string to clipboard
   * @param i
   */
  protected copyServiceToClipboard(i: number) {
    const service: CustomService = this.getCustomServicesFormArray().getRawValue()?.[i];
    const serviceString: string = service.serviceName +
        (service.serviceCategoryId ? ' (' + service.serviceCategoryId + ')' : '') +
        (service.serviceDescription ? ': ' + service.serviceDescription : '');
    if (this.clipboard.copy(serviceString)) {
      this.feedbackService.showSuccessSnackbar({
        data: { userFriendlyMessage: 'Copied.' },
        isAddressListSnackbar: true,
      });
    } else console.error('Error while copying custom service string to clipboard.');
  }

  /**
   * Dynamically prepare mat-label content.
   */
  protected getLabel(formControlName: string): string {
    return capitalizeFirstLetter(getFullNameForFieldID(formControlName));
  }

  /**
   * Get error message
   * @param formGroupName
   * @param formControlName
   */
  protected getErrorMessage(formGroupName: number, formControlName: string): string {
    const errors = this.getCustomServicesFormArray()
        ?.get(formGroupName.toString())
        ?.get(formControlName)
        ?.errors;

    const errorKeys: string[] = Object.keys(errors || []);

    // nothing to return...
    if (!errorKeys.length) {
      return null;
    }

    const firstErrorKey = errorKeys[0];

    switch (firstErrorKey) {
      case ValidationErrorKeys.REQUIRED:
        return 'Required field.';
      case ValidationErrorKeys.MIN_LENGTH:
        const min = errors[firstErrorKey]?.requiredLength;
        return `Minimum of ${min} character(s).`;
      case ValidationErrorKeys.MAX_LENGTH:
        const max = errors[firstErrorKey]?.requiredLength;
        return `Maximum of ${max} character(s).`;
      case ValidationErrorKeys.CONTAINS_ONLY_WHITESPACE:
        return 'Cannot be whitespace-only.';
      default:
        return 'Please check your input.';
    }
  }

  protected getCustomServicesFormArray(): FormArray<FormGroup<CustomServicesTControl>> {
    return this.editorFormGroup?.get('customServices') as FormArray<FormGroup<CustomServicesTControl>>;
  }

  /**
   * Construct a new form group instance.
   */
  protected createCustomServiceFormGroup(): FormGroup<CustomServicesTControl> {
    return this.fb.group({
      'serviceName': [null, customServicesValidators.serviceName],
      'serviceDescription': [null, customServicesValidators.serviceDescription],
      'serviceCategoryId': [null, customServicesValidators.serviceCategoryId],
    }) as FormGroup<CustomServicesTControl>;
  }


}
